import { computed, inject } from "@angular/core";
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from "@ngrx/signals";
import { rxMethod } from "@ngrx/signals/rxjs-interop";
import { catchError, of, pipe, switchMap, tap } from "rxjs";
import {
  ChannelLayoutClient,
  ChannelLayoutDto,
} from "./coremedia-content.client";

interface ChannelLayoutState {
  channelId: string | null;
  status: "idle" | "loading" | "ready" | "error";
  layout: ChannelLayoutDto | null;
  error: unknown;
}

const initialState: ChannelLayoutState = {
  channelId: null,
  status: "idle",
  layout: null,
  error: null,
};

export const channelLayoutStore = signalStore(
  withState(initialState),
  withComputed((store) => ({
    isReady: computed(() => store.status() === "ready"),
    placements: computed(() => store.layout()?.placements ?? []),
    /**
     * True whenever the channel could not be rendered — either the backend call failed (incl.
     * any 5xx) or it succeeded but returned no placements. Raw backend errors must never be
     * shown to users; consumers should treat this as "show the 404 page" instead.
     */
    notFound: computed(
      () =>
        store.status() === "error" ||
        (store.status() === "ready" &&
          (store.layout()?.placements.length ?? 0) === 0),
    ),
  })),
  withMethods((store, client = inject(ChannelLayoutClient)) => ({
    loadChannel: rxMethod<string>(
      pipe(
        tap((id) =>
          patchState(store, { channelId: id, status: "loading", error: null }),
        ),
        switchMap((id) =>
          client.loadLayout(id).pipe(
            tap((layout) => patchState(store, { layout, status: "ready" })),
            catchError((error) => {
              patchState(store, { status: "error", error, layout: null });
              return of(null);
            }),
          ),
        ),
      ),
    ),
    loadChannelWithSiteName: rxMethod<{ channelId: string; siteName: string }>(
      pipe(
        tap(({ channelId }) =>
          patchState(store, { channelId, status: "loading", error: null }),
        ),
        switchMap(({ channelId, siteName }) =>
          client.loadLayoutWithSiteName(channelId, siteName).pipe(
            tap((layout) => patchState(store, { layout, status: "ready" })),
            catchError((error) => {
              patchState(store, { status: "error", error, layout: null });
              return of(null);
            }),
          ),
        ),
      ),
    ),
    loadLocalizedChannel: rxMethod<{ channelId: string; siteName: string }>(
      pipe(
        tap(({ channelId }) =>
          patchState(store, {
            channelId,
            status: "loading",
            error: null,
          }),
        ),
        switchMap(({ channelId, siteName }) =>
          client.loadLocalizedLayout(channelId, siteName).pipe(
            tap((layout) => patchState(store, { layout, status: "ready" })),
            catchError((error) => {
              patchState(store, { status: "error", error, layout: null });
              return of(null);
            }),
          ),
        ),
      ),
    ),
  })),
);
